import { cloneElement, createElement, Fragment, isValidElement, type ReactNode } from "react";
import { jsx as reactJsx, jsxs as reactJsxs, type JSX } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { cashText } from "../cash-language";
export { Fragment, type JSX };

// Cash-only JSX localization. It changes rendered interface text and accessible
// labels, never DOM nodes, controlled values, API payloads or global components.
// Leaf subscriptions ensure the existing language switch updates open dialogs.
function Text({ value }: { value: string }) {
  const { i18n } = useTranslation();
  return cashText(value, i18n.language?.split("-")[0] === "ar");
}
function children(value: ReactNode): ReactNode {
  if (typeof value === "string") return reactJsx(Text, { value });
  if (Array.isArray(value)) return value.map((v, index) => {
    if (typeof v === "string") return reactJsx(Text, { value: v }, index);
    if (Array.isArray(v)) return children(v);
    return isValidElement(v) && v.key == null ? cloneElement(v, { key: index }) : v;
  });
  return value;
}
function Element({ tag, original }: { tag: string; original: any }) {
  const { i18n } = useTranslation();
  const arabic = i18n.language?.split("-")[0] === "ar";
  const props = { ...original };
  if (typeof props.className === "string" && /(?:^|\s)(branch-cash|cb-overlay|expense-setup)(?:\s|$)/.test(props.className)) {
    props.dir = arabic ? "rtl" : "ltr";
    props.lang = arabic ? "ar" : "en";
  }
  for (const key of ["title", "placeholder", "aria-label", "alt", ...(tag === "optgroup" ? ["label"] : [])]) {
    if (typeof props[key] === "string") props[key] = cashText(props[key], arabic);
  }
  if (tag === "option" && props.value === undefined && typeof props.children === "string") props.value = props.children;
  if (props.translate !== "no" && !["script", "style", "textarea"].includes(tag)) props.children = children(props.children);
  return createElement(tag, props);
}
export function jsx(type: any, props: any, key?: any): JSX.Element {
  if (typeof type === "string") return reactJsx(Element, { tag: type, original: props }, key);
  return reactJsx(type, props && { ...props, children: children(props.children) }, key);
}
export function jsxs(type: any, props: any, key?: any): JSX.Element {
  if (typeof type === "string") return reactJsx(Element, { tag: type, original: props }, key);
  return reactJsxs(type, props && { ...props, children: children(props.children) }, key);
}